import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { catchError, map, takeUntil } from 'rxjs/operators';

export interface AuthUser {
  id: string;
  uid: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  rights: string[];
}

interface LoginResponse {
  user: AuthUser;
  token: string;
}

interface TokenStatusResponse {
  expiresIn: number;
  isExpiringSoon: boolean;
  expiresAt: number;
  refreshThresholdSeconds: number;
}

interface RefreshTokenResponse {
  token: string;
  expiresIn: number;
  expiresAt: number;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  private apiUrl = '/api/v1/auth';
  private tokenKey = 'auth_token';
  private userKey = 'auth_user';

  private tokenSubject = new BehaviorSubject<string | null>(this.getStoredToken());
  public token$ = this.tokenSubject.asObservable();

  private userSubject = new BehaviorSubject<AuthUser | null>(this.getStoredUser());
  public user$ = this.userSubject.asObservable();

  // Emit when token is close to expiration - asks user to renew
  private tokenExpiringSubject = new Subject<TokenStatusResponse>();
  public tokenExpiring$ = this.tokenExpiringSubject.asObservable();

  private destroy$ = new Subject<void>();
  private refreshCheckInterval: ReturnType<typeof setInterval> | undefined;

  constructor(private http: HttpClient) {
    this.startTokenExpirationCheck();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.refreshCheckInterval) {
      clearInterval(this.refreshCheckInterval);
    }
  }

  /**
   * Login with email/username and password
   */
  login(emailOrUsername: string, password: string): Observable<LoginResponse> {
    return this.http
      .post<LoginResponse>(`${this.apiUrl}/login`, { emailOrUsername, password })
      .pipe(
        map((response) => {
          this.setToken(response.token);
          this.setUser(response.user);
          this.startTokenExpirationCheck();
          return response;
        }),
        catchError((error) => {
          console.error('Login failed:', error);
          throw error;
        }),
      );
  }

  /**
   * Logout and clear stored credentials
   */
  logout(): void {
    this.clearToken();
    this.clearUser();
    this.tokenSubject.next(null);
    this.userSubject.next(null);
    if (this.refreshCheckInterval) {
      clearInterval(this.refreshCheckInterval);
    }
  }

  /**
   * Check if token is close to expiration
   */
  checkTokenStatus(): Observable<TokenStatusResponse> {
    return this.http.get<TokenStatusResponse>(`${this.apiUrl}/token-status`).pipe(
      catchError((error) => {
        console.error('Failed to check token status:', error);
        throw error;
      }),
    );
  }

  /**
   * Manually refresh the current token (call after user confirms)
   */
  refreshToken(): Observable<RefreshTokenResponse> {
    return this.http.post<RefreshTokenResponse>(`${this.apiUrl}/refresh-token`, {}).pipe(
      map((response) => {
        this.setToken(response.token);
        console.log('Token refreshed successfully');
        return response;
      }),
      catchError((error) => {
        console.error('Token refresh failed:', error);
        this.logout();
        throw error;
      }),
    );
  }

  /**
   * Get current token
   */
  getToken(): string | null {
    return this.tokenSubject.value;
  }

  /**
   * Get current user
   */
  getUser(): AuthUser | null {
    return this.userSubject.value;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  /**
   * Check if user has specific right
   */
  hasRight(right: string): boolean {
    return this.getUser()?.rights?.includes(right) ?? false;
  }

  /**
   * Check if user has specific role
   */
  hasRole(role: string): boolean {
    return this.getUser()?.role === role;
  }

  /**
   * Private: Store token in localStorage and BehaviorSubject
   */
  private setToken(token: string): void {
    localStorage.setItem(this.tokenKey, token);
    this.tokenSubject.next(token);
  }

  /**
   * Private: Store user in localStorage and BehaviorSubject
   */
  private setUser(user: AuthUser): void {
    localStorage.setItem(this.userKey, JSON.stringify(user));
    this.userSubject.next(user);
  }

  /**
   * Private: Clear stored token
   */
  private clearToken(): void {
    localStorage.removeItem(this.tokenKey);
  }

  /**
   * Private: Clear stored user
   */
  private clearUser(): void {
    localStorage.removeItem(this.userKey);
  }

  /**
   * Private: Get stored token from localStorage
   */
  private getStoredToken(): string | null {
    return localStorage.getItem(this.tokenKey);
  }

  /**
   * Private: Get stored user from localStorage
   */
  private getStoredUser(): AuthUser | null {
    const stored = localStorage.getItem(this.userKey);
    return stored ? (JSON.parse(stored) as AuthUser) : null;
  }

  /**
   * Private: Start checking token expiration
   * Checks every 30 seconds and emits tokenExpiring$ when close to expiration
   * User can then decide to renew via refreshToken()
   */
  private startTokenExpirationCheck(): void {
    if (this.refreshCheckInterval) {
      clearInterval(this.refreshCheckInterval);
    }

    if (!this.getToken()) {
      return;
    }

    this.refreshCheckInterval = setInterval(() => {
      this.checkTokenStatus()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (status) => {
            if (status.isExpiringSoon) {
              console.log(`Token expiring in ${status.expiresIn} seconds - asking user to renew`);
              // Emit event - component will show dialog to ask user
              this.tokenExpiringSubject.next(status);
            }
          },
          error: (err) => {
            console.error('Failed to check token status:', err);
            this.logout();
          },
        });
    }, 30 * 1000); // Check every 30 seconds
  }
}
